/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  AnalyticsDescribeRequest,
  AnalyticsDescribeSecurity,
} from "../models/operations/analyticsdescribe.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  AnalyticsDescribeQueryData,
  buildAnalyticsDescribeQuery,
  prefetchAnalyticsDescribe,
  queryKeyAnalyticsDescribe,
} from "./analyticsDescribe.core.js";
export {
  type AnalyticsDescribeQueryData,
  buildAnalyticsDescribeQuery,
  prefetchAnalyticsDescribe,
  queryKeyAnalyticsDescribe,
};

export type AnalyticsDescribeQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * describe analytics
 *
 * @remarks
 * Describe every dataset and the fields it exposes, with the operators and aggregations each admits.
 */
export function useAnalyticsDescribe(
  request?: AnalyticsDescribeRequest | undefined,
  security?: AnalyticsDescribeSecurity | undefined,
  options?: QueryHookOptions<
    AnalyticsDescribeQueryData,
    AnalyticsDescribeQueryError
  >,
): UseQueryResult<AnalyticsDescribeQueryData, AnalyticsDescribeQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildAnalyticsDescribeQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * describe analytics
 *
 * @remarks
 * Describe every dataset and the fields it exposes, with the operators and aggregations each admits.
 */
export function useAnalyticsDescribeSuspense(
  request?: AnalyticsDescribeRequest | undefined,
  security?: AnalyticsDescribeSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    AnalyticsDescribeQueryData,
    AnalyticsDescribeQueryError
  >,
): UseSuspenseQueryResult<
  AnalyticsDescribeQueryData,
  AnalyticsDescribeQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildAnalyticsDescribeQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setAnalyticsDescribeData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: AnalyticsDescribeQueryData,
): AnalyticsDescribeQueryData | undefined {
  const key = queryKeyAnalyticsDescribe(...queryKeyBase);

  return client.setQueryData<AnalyticsDescribeQueryData>(key, data);
}

export function invalidateAnalyticsDescribe(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      gramSession?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "analytics", "describe", ...queryKeyBase],
  });
}

export function invalidateAllAnalyticsDescribe(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "analytics", "describe"],
  });
}
